import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Tab, Modal, Button, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FormIDS from "../../../../components/Struttura/LaMiaStruttura/formIDS";
import FormSport from "../../../../components/Struttura/LaMiaStruttura/formSport";
import FormTitoli from "../../../../components/Struttura/LaMiaStruttura/formTitoli";
import FormFoto from "../../../../components/Struttura/LaMiaStruttura/formFoto";
import FormFitCoin from "../../../../components/Struttura/LaMiaStruttura/formFitcoin";
import FormFatturazione from "../../../../components/Struttura/LaMiaStruttura/formFatturazione";
import FormSchedaApp from "../../../../components/Struttura/SchedaApp/FormSchedaApp";
import VisibilitaNelleListe from "../../../../components/Struttura/VisibilitaNelleListe";
import NavTabAdattiva from "../../../../components/Util/NavTabAdattiva";
import { SUPERADMIN_PUO_MODIFICARE } from "../../../../components/Util/modificaSuperAdmin";
import MyApiClient, { impostaProfiloDiModifica } from "../../../../../api-client/MyApiClient";
import { usePageTitle, useStatoInSolaLettura, statoProfilo } from "../../../../layouts/PageHeaderExtra";

/*
 * Scheda di una struttura per il super admin. Con REACT_APP_SUPERADMIN_MODIFICA=S i tab dei dati sono quelli
 * di «La mia struttura» e si modificano come li modifica lei: i salvataggi vanno sulle rotte del super admin
 * (PATCH /administration/profiles/{id} e `?userId=` per foto e titoli, impostaProfiloDiModifica).
 */

const puoModificare = SUPERADMIN_PUO_MODIFICARE;

export const StrutturaEdit = () => {

	const { id } = useParams();
	const navigate = useNavigate();
	// «Elimina struttura»: era nel menu a fine riga della lista, ora sta nella scheda (03/10/2026).
	const [confermaElimina, setConfermaElimina] = useState(false);

	const [profile, setProfile] = useState(null);
	const nome = profile?.club?.name || profile?.name || '';
	// Titolo della pagina = nome della struttura (regola SUI, docs/sportid-ui.md capitolo 6)
	usePageTitle(nome || undefined);
	// Stato del profilo in sola lettura accanto al titolo (manca l'API per cambiarlo)
	const statoProf = statoProfilo(profile?.club?.profileStatus || profile?.profileStatus);
	useStatoInSolaLettura(statoProf.label, statoProf.tono);
	const [reloadKey, setReloadKey] = useState(0);
	const [loadingShowed, setLoadingShowed] = useState(false);

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	// Dopo un salvataggio si ricaricano i dati e si resta nel tab
	const ricarica = useCallback(() => setReloadKey(k => k + 1), []);
	const datiComuni = { profile, isDisabled: !puoModificare, superAdmin: true, onSalvato: ricarica };

	// Finche' la scheda e' aperta i salvataggi valgono per questa struttura
	useEffect(() => {
		if (!puoModificare) return undefined;
		impostaProfiloDiModifica(id);
		return () => impostaProfiloDiModifica(null);
	}, [id]);

	const tabData = [
		{
			id: 'ids',
			name: "IDS",
			content: <FormIDS {...datiComuni} />,
		},
		{
			id: 'sport',
			name: "Sport",
			content: <FormSport {...datiComuni} />,
		},
		{
			id: 'copertina',
			name: "Foto e video",
			content: <FormFoto {...datiComuni} clubId={id} />,
		},
		{
			id: 'scheda-app',
			name: "Scheda nelle App",
			content: <FormSchedaApp profile={profile} disabilitato={!puoModificare} />,
		},
		{
			// Come in «La mia struttura» i titoli nuovi non si caricano piu': restano quelli gia' caricati
			id: 'titoli',
			name: "Titoli",
			content: <FormTitoli {...datiComuni} soloEsistenti />,
		},
		{
			id: 'fitcoins',
			name: "FitCoins",
			content: <FormFitCoin profile={profile} isDisabled={true} />,
		},
		{
			id: 'fatturazione',
			name: "Documenti fiscali",
			content: <FormFatturazione profile={profile} isSuperAdmin={true} userId={id} />,
		},
		{
			// Solo super admin: la struttura si mostra o no nelle liste delle app (14/10/2026)
			id: 'visibilita',
			name: "Visibilità",
			content: <VisibilitaNelleListe
				clubId={id}
				nascosta={profile?.club?.hiddenFromLists ?? profile?.hiddenFromLists}
				onErrore={(motivo) => { setModalTitle('Visibilità non cambiata'); setModalText(motivo); setModalShowed(true); }}
			/>,
		}
	];

	const location = useLocation();
	const tabDallIndirizzo = () => {
		const tab = new URLSearchParams(location.search).get('tab');
		return tabData.some(t => t.id === tab) ? tab : tabData[0].id;
	};
	const [tabAperto, setTabAperto] = useState(tabDallIndirizzo);
	useEffect(() => {
		setTabAperto(tabDallIndirizzo());
	}, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

	const eliminaStruttura = async () => {
		setConfermaElimina(false);
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return;
		const token = JSON.parse(tokenDetailsString).idToken;
		setLoadingShowed(true);
		const deleteResult = await new MyApiClient().deleteProfile(token, id);
		setLoadingShowed(false);
		if (deleteResult) {
			setModalTitle("Errore durante l'operazione");
			setModalText(deleteResult);
			setModalShowed(true);
		} else {
			navigate('/superadmin/anagrafiche/strutture-sportive');
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			const tokenDetailsString = sessionStorage.getItem('userDetails');
			if (!tokenDetailsString) return;
			const token = JSON.parse(tokenDetailsString).idToken;
			const apiClient = new MyApiClient();
			setLoadingShowed(true);
			try {
				// Il profilo completo, lo stesso che vede la struttura (GET /profile?userId=): i moduli lo
				// rimandano intero quando salvano. Se non arriva, quello pubblico (solo da guardare).
				const [completo, titoli] = await Promise.all([
					apiClient.getPrivateProfile(token, id).catch(() => null),
					apiClient.getTitles(token, id).catch(() => null),
				]);
				const profilo = completo?.club ? completo : await apiClient.getProfiles(token, id);
				if (profilo?.club && Array.isArray(titoli)) profilo.club.titles = titoli;
				setProfile(profilo);
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoadingShowed(false);
			}
		};

		fetchData();
	}, [id, reloadKey]);

	return (
		<>
			<Modal className="fade" show={modalShowed} onHide={() => setModalShowed(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{modalTitle}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
						Chiudi
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<div className="custom-tab-1">
				<Tab.Container activeKey={tabAperto} onSelect={(k) => k && setTabAperto(k)}>
					<NavTabAdattiva tabs={tabData} activeKey={tabAperto} onSelect={setTabAperto} />
					<Tab.Content className="mt-4">
						{tabData.map((data) => (
							<Tab.Pane eventKey={data.id} key={data.id}>
								{data.content}
							</Tab.Pane>
						))}
					</Tab.Content>
				</Tab.Container>
			</div>

			{/* Azioni della struttura: in fondo alla scheda, l'eliminazione a sinistra. */}
			<div className="d-flex justify-content-between mt-4">
				<Button variant="danger" onClick={() => setConfermaElimina(true)}>Elimina struttura</Button>
				<span />
			</div>

			<Modal className="fade" show={confermaElimina} onHide={() => setConfermaElimina(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Eliminare la struttura?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Eliminare la struttura «{nome || 'senza nome'}»? L'operazione non si può annullare.
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-light btn-add" onClick={() => setConfermaElimina(false)}>Annulla</Button>
					<Button variant="danger" onClick={eliminaStruttura}>Elimina</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}
export default StrutturaEdit;
